import {
	createBlueprint,
	useBlueprint,
} from "../../_nextgen/packages/zustand-blueprints";
import type { Vehicle } from "../../api/dd365/index.types";

interface SearchInventoryToolStoreStore<T = Record<string, unknown>> {
	id: string;
	initialValues?: T;
}

interface Filter {
	label: string;
	code: string;
}

export interface FilterState {
	[key: string]: string[] | string;
}

export interface NavigationState {
	[key: string]: string;
}

interface searchInventoryToolBlueprintType {
	// VLP Filters
	vehicleFilterState: FilterState;
	setVehicleFilterState: (
		filterState: FilterState | ((filterState: FilterState) => FilterState),
	) => void;
	// VLP search params navigation
	vehicleNavigationState: NavigationState;
	setVehicleNavigationState: (
		navigationState:
			| NavigationState
			| ((navigationState: NavigationState) => NavigationState),
	) => void;
	// VLP Compare Vehicle
	compareVehicles: Vehicle[];
	setCompareVehicles: (vehicles: Vehicle[]) => void;
	isFullCompareVehicles: boolean;
	updateIsFullCompareVehicles: (isFull: boolean) => void;
}

const removeEmptyArrays = (obj: any): any => {
	return Object.keys(obj).reduce((acc, key) => {
		if (obj[key].length > 0) {
			acc[key] = obj[key];
		}
		return acc;
	}, {});
};

const searchInventoryToolStore =
	createBlueprint<searchInventoryToolBlueprintType>((set) => {
		return {
			// VLP Filters
			vehicleFilterState: {},
			setVehicleFilterState: (
				filterState: FilterState | ((filterState: FilterState) => FilterState),
			) =>
				set(({ vehicleFilterState }) => {
					if (typeof filterState === "object") {
						const newObj = filterState;
						return { vehicleFilterState: removeEmptyArrays(newObj) };
					}
					const setterFn: (filterState: FilterState) => FilterState =
						filterState;
					const newObj = setterFn(vehicleFilterState);
					return { vehicleFilterState: removeEmptyArrays(newObj) };
				}),
			// VLP search params navigation
			vehicleNavigationState: {},
			setVehicleNavigationState: (
				navigationState:
					| NavigationState
					| ((navigationState: NavigationState) => NavigationState),
			) =>
				set(({ vehicleNavigationState }) => {
					if (typeof navigationState === "object") {
						const newObj = navigationState;
						return { vehicleNavigationState: removeEmptyArrays(newObj) };
					}
					const setterFn: (
						navigationState: NavigationState,
					) => NavigationState = navigationState;
					const newObj = setterFn(vehicleNavigationState);
					return { vehicleNavigationState: removeEmptyArrays(newObj) };
				}),
			// VLP Compare Vehicle
			compareVehicles: [],
			setCompareVehicles: (vehicles) => set({ compareVehicles: vehicles }),
			isFullCompareVehicles: false,
			updateIsFullCompareVehicles: (isFull) =>
				set({ isFullCompareVehicles: isFull }),
		};
	});

export const useSearchInventoryToolStore = ({
	id,
	initialValues,
}: SearchInventoryToolStoreStore) =>
	useBlueprint({
		id,
		blueprint: searchInventoryToolStore,
		initialValues: {
			...initialValues,
		},
	});

export const compareVehicleHandlerStorage = (
	modelName: string | undefined = undefined,
) => {
	const getCompareVehicleListStorage = () => {
		const compareVehicleStorage = sessionStorage.getItem(
			"SIT_COMPARE_VEHICLE_LIST",
		);
		return compareVehicleStorage && JSON.parse(compareVehicleStorage);
	};

	const setCompareVehicleListStorage = (data) => {
		sessionStorage.setItem("SIT_COMPARE_VEHICLE_LIST", JSON.stringify(data));
	};

	const getCompareVehicleModelStorage = () => {
		const compareVehicleStorage = sessionStorage.getItem(
			"SIT_COMPARE_VEHICLE_MODEL",
		);
		return compareVehicleStorage && JSON.parse(compareVehicleStorage);
	};

	const setCompareVehicleModelStorage = (data) => {
		sessionStorage.setItem("SIT_COMPARE_VEHICLE_MODEL", JSON.stringify(data));
	};

	const getCompareVehicleCookieArray = (): Vehicle[] | [] => {
		const compareVehicleModelCookie = getCompareVehicleModelStorage();
		if (compareVehicleModelCookie !== modelName && modelName !== undefined) {
			setCompareVehicleListStorage([]);
			return [];
		}
		const compareVehicle = getCompareVehicleListStorage();
		const compareVehicleArray =
			compareVehicle &&
			typeof compareVehicle === "object" &&
			Array.isArray(compareVehicle)
				? ([...new Set(compareVehicle)] as Vehicle[])
				: [];
		return compareVehicleArray;
	};

	const addToCompareVehicleCookie = (vehicles: Vehicle[]) => {
		if (modelName !== undefined) setCompareVehicleModelStorage(modelName);
		setCompareVehicleListStorage(vehicles);
	};

	return [getCompareVehicleCookieArray, addToCompareVehicleCookie];
};

export const previouslyViewedHandlerStorage = (
	numberPreviouslyViewed: number,
) => {
	const getPreviouslyViewed = () => {
		const previouslyViewedStorage = localStorage.getItem(
			"SIT_PREVIOUSLY_VIEWED",
		);
		return previouslyViewedStorage && JSON.parse(previouslyViewedStorage);
	};

	const setPreviouslyViewed = (data: Vehicle[]) => {
		localStorage.setItem("SIT_PREVIOUSLY_VIEWED", JSON.stringify(data));
	};

	const clearPreviouslyViewed = (previouslyViewed: Vehicle[]) => {
		if (previouslyViewed.length > numberPreviouslyViewed) {
			previouslyViewed.splice(numberPreviouslyViewed, previouslyViewed.length);
		}
		setPreviouslyViewed(previouslyViewed);
		return previouslyViewed;
	};

	const getPreviouslyViewedArray = () => {
		const previouslyViewed = getPreviouslyViewed();
		const previouslyViewedArray: Vehicle[] | [] =
			previouslyViewed &&
			typeof previouslyViewed === "object" &&
			Array.isArray(previouslyViewed)
				? ([...new Set(previouslyViewed)] as Vehicle[])
				: [];
		return clearPreviouslyViewed(previouslyViewedArray);
	};

	const vehicleIsOnPreviouslyViewed = (vin?: string) => {
		const index = getPreviouslyViewedArray()?.findIndex((i) => i.vin === vin);
		const isOnPreviously = index !== -1;
		return { index, isOnPreviously };
	};

	const addToPreviouslyViewed = (vehicle) => {
		const previouslyViewed = getPreviouslyViewedArray();
		const vehicleClicked: Vehicle = {
			vin: vehicle?.vin,
			dealer: {
				isSmartPath: vehicle?.dealer?.isSmartPath,
				dealerSiteURL: vehicle?.dealer?.dealerSiteURL,
			},
			modelUrl: vehicle?.modelUrl,
		};
		const { index, isOnPreviously } = vehicleIsOnPreviouslyViewed(
			vehicleClicked.vin,
		);
		if (isOnPreviously) {
			previouslyViewed.splice(index, 1);
			previouslyViewed.unshift(vehicleClicked);
		} else {
			previouslyViewed.unshift(vehicleClicked);
		}
		if (previouslyViewed.length > numberPreviouslyViewed)
			previouslyViewed.pop();
		setPreviouslyViewed(previouslyViewed);
	};

	const removeToPreviouslyViewed = (vehicles: Vehicle[]) => {
		const previouslyViewed = getPreviouslyViewedArray();
		const removedPreviouslyViewed = previouslyViewed.filter(
			(i) => !vehicles.some((j) => j.vin === i.vin),
		);
		setPreviouslyViewed(removedPreviouslyViewed);
	};

	return {
		getPreviouslyViewedArray,
		addToPreviouslyViewed,
		vehicleIsOnPreviouslyViewed,
		removeToPreviouslyViewed,
	};
};

export const getPreviousParams = (excludedParams, searchParams) => {
	const params = { ...searchParams };
	const paramsToDelete = Object.keys(params).filter((i) =>
		excludedParams.includes(i),
	);
	paramsToDelete.forEach((i) => delete params[i]);
	return params;
};

const clearCodeParams = (newParams, searchParams) => {
	const params = { ...searchParams };
	if (Object.keys(newParams).length === 0) {
		Object.keys(searchParams).forEach((i) => {
			if (i.includes("Code")) delete params[i];
		});
		return params;
	}
	const paramsToDelete = Object.keys(newParams).filter(
		(i) =>
			Object.keys(searchParams).includes(`${i}Code`) ||
			searchParams[i]?.length === 1 ||
			newParams[i]?.length === 0,
	);
	paramsToDelete.forEach((i) => {
		delete params[i];
		delete params[`${i}Code`];
	});
	return params;
};

const clearEmptyParams = (searchParams) => {
	const params = { ...searchParams };
	const paramsToDelete = Object.keys(params).filter(
		(i) => searchParams[i]?.length === 0,
	);
	paramsToDelete.forEach((i) => delete params[i]);
	return params;
};

export const updateSearchParams = (
	excludedParams: string[],
	searchParams,
	newParams,
	remainPreviousParams = true,
	replaceUrl = true,
) => {
	const previousParams = excludedParams
		? getPreviousParams(excludedParams, searchParams)
		: searchParams;
	const cleanCodeParams = clearCodeParams(newParams, previousParams);
	const cleanEmptyNewParams = clearEmptyParams(newParams);
	if (remainPreviousParams)
		return {
			search: { ...cleanCodeParams, ...cleanEmptyNewParams },
			resetScroll: false,
			replace: replaceUrl,
		};
	return {
		search: { ...cleanEmptyNewParams },
		resetScroll: false,
		replace: replaceUrl,
	};
};

export const setAllToNull = (obj) => {
	let newObj = { ...obj };
	if (Array.isArray(obj)) newObj = [...obj];
	for (const prop in newObj) {
		newObj[prop] = null;
	}
	return newObj;
};

export const arePropsEqual = (_name) => (prevProps, nextProps) => {
	const allKeys = Object.keys({ ...prevProps, ...nextProps });
	const changes = {};

	let propsAreEqual = true;

	allKeys.forEach((key) => {
		if (prevProps[key] !== nextProps[key]) {
			changes[key] = {
				from: prevProps[key],
				to: nextProps[key],
			};
			propsAreEqual = false;
		}
	});

	if (Object.keys(changes).length) {
		console.log("Prop changes:", _name, changes);
	}

	return propsAreEqual;
};

export const isElementInView = (element: HTMLElement) => {
	const rect: DOMRect = element.getBoundingClientRect();
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <=
			(window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth)
	);
};

export const convertToBoolean = (variable) => {
	if (typeof variable === "boolean") {
		return variable;
	}
	if (variable === 1 || variable?.toLowerCase() === "true") {
		return true;
	}
	return false;
};

export const convertBooleanToString = (variable) => {
	if (typeof variable !== "boolean") {
		return variable;
	}
	if (variable) {
		return "true";
	}
	return "false";
};

export const generateDealerUrlWithIntSrc = (
	vdpUrl,
	dealerSiteURL,
	withIntsrcSearchParams,
) => {
	const baseUrl = vdpUrl || dealerSiteURL;
	return baseUrl
		? withIntsrcSearchParams(baseUrl, {
				destination: vdpUrl ? "inventory" : "home",
				dealerPageType: vdpUrl ? "Inventory" : "HomePage",
			})
		: undefined;
};
